import { Controller } from "@hotwired/stimulus"
import { MediaLoader } from "../components/media_loader"

export default class extends Controller {
  static targets = ["editor"]
  static values = {
    uploadUrl: { type: String, default: "/media_file/upload" },
    maxFileSize: { type: Number, default: 100 }, // MB
    allowedTypes: { type: String, default: "image/*,video/*" }
  }



  connect() {
    
    this.initializeSummernote()
    
    // Initialize media loader for this editor
    try {
      this.mediaLoader = new MediaLoader(this.editorTarget, {
        width: 300,
        height: 200,
        showProgress: true,
        showFilename: true
      })
    } catch (error) {
      this.mediaLoader = new SimpleLoader(this.editorTarget)
    }
  }

  disconnect() {
    if (this.summernoteInstance) {
      $(this.editorTarget).summernote('destroy')
    }
    
    // Clean up media loader
    if (this.mediaLoader) {
      this.mediaLoader.destroy()
    }
  }

  initializeSummernote() {
    const $editor = $(this.editorTarget)
    
    $editor.summernote({
      height: 500,
      toolbar: [
        ['style', ['bold', 'italic', 'underline', 'clear']],
        ['font', ['strikethrough', 'superscript', 'subscript']],
        ['color', ['color']],
        ['para', ['ul', 'ol', 'paragraph']],
        ['height', ['height']],
        ['table', ['table']],
        ['insert', ['link', 'picture', 'video', 'media']], // Added 'media' for unified upload
      ],
      callbacks: {
        onImageUpload: (files) => {
          // Handle image uploads
          for (let i = 0; i < files.length; i++) {
            this.uploadFile(files[i], 'image')
          }
        },
        onMediaDelete: (target) => {
          // Handle media deletion (optional)
        }
      }
    })

    this.summernoteInstance = $editor.data('summernote')
    
    // Override video button after Summernote is initialized
    this.overrideVideoButton()
    this.handleUnifiedMediaUpload()
  }

  uploadFile(file, type) {
    
    // Validate file before upload
    if (!this.validateFile(file, type)) {
      return
    }

    // Create FormData for file upload
    const formData = new FormData()
    formData.append('file', file)

    // Show loading indicator with filename
    this.mediaLoader.show(file.name)

    // Use XMLHttpRequest for real upload progress tracking
    this.uploadWithProgress(formData)
      .then(data => {
        if (data.success) {
          // Insert the uploaded media into the editor
          if (type === 'image') {
            this.insertImageIntoEditor(data.url, data.filename)
          } else if (type === 'video') {
            this.insertVideoIntoEditor(data.url, data.filename)
          }
          
          // Show success message
          this.showMessage('File uploaded successfully!', 'success')
        } else {
          // Remove loading indicator on error
          this.mediaLoader.hide()
          this.showMessage('Upload failed: ' + (data.error || 'Unknown error'), 'error')
        }
      })
      .catch(error => {
        // Remove loading indicator on error
        this.mediaLoader.hide()
        this.showMessage('Upload failed: ' + error.message, 'error')
      })
  }

  validateFile(file, type) {
    // Check file size
    const maxSize = this.maxFileSizeValue * 1024 * 1024 // Convert MB to bytes
    if (file.size > maxSize) {
      this.showMessage(`File size exceeds maximum allowed size of ${this.maxFileSizeValue}MB`, 'error')
      return false
    }

    // Check file type
    const allowedTypes = this.allowedTypesValue.split(',')
    const isAllowed = allowedTypes.some(allowedType => {
      if (allowedType.includes('*')) {
        const baseType = allowedType.split('/')[0]
        return file.type.startsWith(baseType + '/')
      }
      return file.type === allowedType
    })

    if (!isAllowed) {
      this.showMessage(`File type ${file.type} is not allowed`, 'error')
      return false
    }

    return true
  }

  uploadWithProgress(formData) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest()
      
      // Track upload progress
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percentage = Math.round((e.loaded / e.total) * 100)
          this.mediaLoader.updateProgress(percentage)
        }
      })
      
      // Handle response
      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const data = JSON.parse(xhr.responseText)
            resolve(data)
          } catch (error) {
            reject(new Error('Invalid JSON response'))
          }
        } else {
          reject(new Error(`HTTP ${xhr.status}: ${xhr.statusText}`))
        }
      })
      
      // Handle errors
      xhr.addEventListener('error', () => {
        reject(new Error('Network error occurred'))
      })
      
      // Handle timeout
      xhr.addEventListener('timeout', () => {
        reject(new Error('Request timed out'))
      })
      
      // Open and send request
      xhr.open('POST', this.uploadUrlValue)
      xhr.setRequestHeader('X-CSRF-Token', document.querySelector('meta[name="csrf-token"]').getAttribute('content'))
      xhr.send(formData)
    })
  }

  insertImageIntoEditor(imageUrl, filename) {
    try {
      
      // Create image HTML element with proper styling
      const imageHtml = `
        <div style="margin: 10px 0;">
          <img src="${imageUrl}" alt="${filename}" style="max-width: 100%; height: auto; border-radius: 4px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);" />
          <p style="margin-top: 5px; font-size: 12px; color: #666;"><em>${filename}</em></p>
        </div>
      `
      
      // Replace loading indicator with image
      if (this.mediaLoader.isVisible()) {
        this.mediaLoader.replaceWithHTML(imageHtml)
      } else {
        // Insert image using Summernote's insertHTML method
        const $editor = $(this.editorTarget)
        $editor.summernote('insertHTML', imageHtml)
      }

      // update textarea value
      $(this.editorTarget).summernote('code', $(this.editorTarget).summernote('code'))
      
    } catch (error) {
      
      // Remove loading indicator and show fallback
      this.mediaLoader.hide()
      
      // Fallback: use Summernote's built-in insertImage
      $(this.editorTarget).summernote('insertImage', imageUrl, filename)
    }
  }

  insertVideoIntoEditor(videoUrl, filename) {
    try {
      
      // Create video HTML element with proper styling
      const videoHtml = `
        <div style="margin: 10px 0;">
          <video controls style="max-width: 100%; height: auto; border-radius: 4px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
            <source src="${videoUrl}" type="video/mp4">
            <source src="${videoUrl}" type="video/webm">
            <source src="${videoUrl}" type="video/ogg">
            Your browser does not support the video tag.
          </video>
          <p style="margin-top: 5px; font-size: 12px; color: #666;"><em>${filename}</em></p>
        </div>
      `
      
      // Replace loading indicator with video
      if (this.mediaLoader.isVisible()) {
        this.mediaLoader.replaceWithHTML(videoHtml)
      } else {
        // Insert video using Summernote's insertHTML method
        const $editor = $(this.editorTarget)
        $editor.summernote('insertHTML', videoHtml)
      }

      // update textarea value
      $(this.editorTarget).summernote('code', $(this.editorTarget).summernote('code'))
      
    } catch (error) {
      
      // Remove loading indicator and show fallback
      this.mediaLoader.hide()
      
      // Fallback: insert as a link
      const fallbackHtml = `<p><a href="${videoUrl}" target="_blank" style="color: #007bff;">${filename}</a> (Video)</p>`
      $(this.editorTarget).summernote('insertHTML', fallbackHtml)
      
      this.showMessage('Video uploaded but could not be embedded. Added as link instead.', 'warning')
    }
  }

  overrideVideoButton() {
    const self = this
    
    // Wait for Summernote to be fully initialized
    setTimeout(() => {
      // Try multiple selectors to find the video button
      let $videoButton = null
      
      // Method 1: Look in the toolbar next to the editor
      const $toolbar = $(this.editorTarget).next('.note-editor').find('.note-toolbar')
      $videoButton = $toolbar.find('.note-btn[data-event="video"]')
      
      // Method 2: Look for any video button on the page
      if (!$videoButton.length) {
        $videoButton = $('.note-btn[data-event="video"]')
      }
      
      // Method 3: Look for button with video icon or text
      if (!$videoButton.length) {
        $videoButton = $('.note-btn').filter(function() {
          return $(this).text().toLowerCase().includes('video') || 
                 $(this).find('i').hasClass('note-icon-video') ||
                 $(this).find('i').hasClass('fa-video') ||
                 $(this).find('i').hasClass('fas fa-video')
        })
      }
      
      
      if ($videoButton.length) {
        // Remove any existing click handlers
        $videoButton.off('click')
        
        // Add our custom video upload handler
        $videoButton.on('click', function(e) {
          e.preventDefault()
          e.stopPropagation()
          self.showVideoUploadDialog()
        })
        
      } else {
        // Retry after a short delay, but limit retries
        if (!this.retryCount) this.retryCount = 0
        this.retryCount++
        
        if (this.retryCount < 10) {
          setTimeout(() => self.overrideVideoButton(), 500)
        } else {
          // Fallback: create a custom video upload button
          self.createCustomVideoButton()
        }
      }
    }, 500)
  }

  createCustomVideoButton() {
    
    // Find the toolbar and add a custom video button
    const $toolbar = $(this.editorTarget).next('.note-editor').find('.note-toolbar')
    if ($toolbar.length) {
      const customButton = `
        <button type="button" class="note-btn note-btn-video-custom" data-event="video-upload" title="Upload Video">
          <i class="fas fa-video"></i>
        </button>
      `
      $toolbar.find('.note-btn-group:last').append(customButton)
      
      // Add click handler to custom button
      $toolbar.find('.note-btn-video-custom').on('click', (e) => {
        e.preventDefault()
        this.showVideoUploadDialog()
      })
      
    }
  }

  showVideoUploadDialog() {
    // Create a file input for video upload
    const fileInput = document.createElement('input')
    fileInput.type = 'file'
    fileInput.accept = 'video/*'
    fileInput.style.display = 'none'
    
    fileInput.addEventListener('change', (event) => {
      const file = event.target.files[0]
      if (file) {
        this.uploadFile(file, 'video')
      }
      document.body.removeChild(fileInput)
    })
    
    document.body.appendChild(fileInput)
    fileInput.click()
  }

  handleUnifiedMediaUpload() {
    // Add custom media upload button functionality
    $(document).on('click', '.note-btn[data-event="media"]', (e) => {
      // Only handle if this is our editor
      if (!$(e.target).closest(this.element).length) return
      
      e.preventDefault()
      e.stopPropagation()
      
      // Create a file input for media upload (images and videos)
      const fileInput = document.createElement('input')
      fileInput.type = 'file'
      fileInput.accept = 'image/*,video/*'
      fileInput.multiple = true
      fileInput.style.display = 'none'
      
      fileInput.addEventListener('change', (event) => {
        const files = Array.from(event.target.files)
        files.forEach(file => {
          const type = file.type.startsWith('image/') ? 'image' : 'video'
          this.uploadFile(file, type)
        })
        document.body.removeChild(fileInput)
      })
      
      document.body.appendChild(fileInput)
      fileInput.click()
    })
  }

  showMessage(message, type) {
    const messageClass = type === 'success' ? 'alert-success' : type === 'warning' ? 'alert-warning' : 'alert-danger'
    const iconClass = type === 'success' ? 'fa-check-circle' : type === 'warning' ? 'fa-exclamation-triangle' : 'fa-exclamation-circle'
    
    const messageHtml = `
      <div class="alert ${messageClass} alert-dismissible fade show" role="alert">
        <i class="fas ${iconClass} mr-2"></i>
        ${message}
        <button type="button" class="close" data-dismiss="alert" aria-label="Close">
          <span aria-hidden="true">&times;</span>
        </button>
      </div>
    `
    
    // Insert message before the editor
    this.editorTarget.insertAdjacentHTML('beforebegin', messageHtml)
    
    // Auto-dismiss after 5 seconds
    setTimeout(() => {
      const alert = this.editorTarget.previousElementSibling
      if (alert && alert.classList.contains('alert')) {
        alert.style.transition = 'opacity 0.5s'
        alert.style.opacity = '0'
        setTimeout(() => alert.remove(), 500)
      }
    }, 5000)
  }
}
